import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { esignatureService } from "../../services/esignature";
import * as pdfjsLib from "pdfjs-dist";
import PageContent from "../shared/PageContent/PageContent";
import "../Recruiting/RecruitingDashboard.css";
import "./DocumentList.css";

// Worker is configured globally in index.js - no need to set it here

const TemplateThumbnail = ({ pdfUrl }) => {
  const canvasRef = useRef(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setThumbnailLoading(true);
        setError(false);

        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setThumbnailLoading(false);
      } catch (err) {
        setError(true);
        setThumbnailLoading(false);
      }
    };

    if (pdfUrl) {
      loadPDF();
    }
  }, [pdfUrl]);

  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current) return;

      try {
        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        const viewport = page.getViewport({ scale: 0.5 });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;
      } catch (err) {
        // Error rendering PDF page
      }
    };

    renderPage();
  }, [pdfDoc, currentPage]);

  const handlePrevPage = e => {
    e.stopPropagation();
    e.preventDefault();
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = e => {
    e.stopPropagation();
    e.preventDefault();
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (error) {
    return (
      <div className="template-thumbnail-placeholder">
        <p>Preview unavailable</p>
      </div>
    );
  }

  return (
    <div className="template-thumbnail">
      {thumbnailLoading && (
        <div className="template-thumbnail-placeholder">
          <span>Loading...</span>
        </div>
      )}
      {!thumbnailLoading && (
        <>
          <canvas ref={canvasRef} />
          {totalPages > 1 && (
            <div className="pdf-page-controls">
              <button
                className="pdf-nav-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                ←
              </button>
              <span className="pdf-page-info">
                {currentPage} / {totalPages}
              </span>
              <button
                className="pdf-nav-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TemplateList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getVerifiedJWT } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const recruitingTabs = [
    { label: "Dashboard", path: "/recruiting" },
    { label: "Pipeline", path: "/recruiting/pipeline" },
    { label: "Lists", path: "/recruiting/list" },
    { label: "Documents", path: "/recruiting/documents" },
    { label: "Templates", path: "/esignature/templates" },
  ];

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const result = await esignatureService.getTemplates(jwt);

      // Ensure we always set an array
      let templatesArray = [];
      if (Array.isArray(result)) {
        templatesArray = result;
      } else if (result && Array.isArray(result.data)) {
        templatesArray = result.data;
      } else if (result && Array.isArray(result.templates)) {
        templatesArray = result.templates;
      } else if (
        result &&
        result.data &&
        Array.isArray(result.data.templates)
      ) {
        templatesArray = result.data.templates;
      } else if (result && typeof result === "object") {
        // Backend might be returning an object with templates as keys

        // Try to extract templates from object values if it's an object
        const values = Object.values(result.data || result);
        if (values.length > 0 && values[0] && typeof values[0] === "object") {
          templatesArray = values;
        } else {
          templatesArray = [];
        }
      }

      setTemplates(templatesArray);
    } catch (error) {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleCreateTemplate = () => {
    setShowUploadModal(true);
  };

  const handleEditTemplate = (e, template) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/esignature/templates/${template.id}/edit`);
  };

  const handleUseTemplate = (e, template) => {
    e.stopPropagation();
    e.preventDefault();
    // Navigate to recruiting pipeline page with template pre-selected
    navigate(
      `/recruiting/pipeline?templateId=${template.id}&openSendDocs=true`
    );
  };

  return (
    <PageContent
      customHeader={
        <div className="recruiting-page-custom-header">
          <div className="recruiting-page-header-left">
            <h1 className="recruiting-page-main-title">Recruiting</h1>
            <div className="recruiting-page-nav-tabs">
              {recruitingTabs.map(tab => (
                <button
                  key={tab.path}
                  className={`recruiting-page-nav-tab ${
                    location.pathname === tab.path ||
                    (tab.path === "/esignature/templates" &&
                      location.pathname.startsWith("/esignature/templates"))
                      ? "active"
                      : ""
                  }`}
                  onClick={() => navigate(tab.path)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >
      <div className="document-list-container">
        <div className="list-header">
          <div>
            <h1>Document Templates</h1>
            <p className="subtitle">
              Create reusable templates for frequently used documents
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button className="btn-upload" onClick={handleCreateTemplate}>
              + Create Template
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            <h3>No templates found</h3>
            <p>
              Create a template to reuse documents with pre-configured fields
            </p>
            <button className="btn-upload-large" onClick={handleCreateTemplate}>
              Create Your First Template
            </button>
          </div>
        ) : (
          <div className="documents-grid">
            {Array.isArray(templates) &&
              templates.map(template => (
                <div key={template.id} className="document-card template-card">
                  <div className="template-card-content">
                    <div className="card-header">
                      <h3>{template.name}</h3>
                    </div>
                    {template.pdf_url && (
                      <TemplateThumbnail pdfUrl={template.pdf_url} />
                    )}
                    <div className="template-metadata">
                      <div className="metadata-item">
                        <span className="metadata-label">Owner:</span>
                        <span className="metadata-value">
                          {template.created_by_user?.first_name &&
                          template.created_by_user?.last_name
                            ? `${template.created_by_user.first_name} ${template.created_by_user.last_name}`
                            : template.created_by_user?.email || "N/A"}
                        </span>
                      </div>
                      <div className="metadata-item">
                        <span className="metadata-label">Created:</span>
                        <span className="metadata-value">
                          {formatDate(template.created_at)}
                        </span>
                      </div>
                      <div className="metadata-item">
                        <span className="metadata-label">Modified:</span>
                        <span className="metadata-value">
                          {formatDate(template.updated_at)}
                        </span>
                      </div>
                    </div>
                    <div className="template-actions">
                      <button
                        className="edit-template-btn"
                        onClick={e => handleEditTemplate(e, template)}
                        style={{
                          backgroundColor: "#f5f5f5",
                          color: "#666",
                          border: "1px solid #ddd",
                          borderRadius: "6px",
                          padding: "10px 16px",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          flex: 1,
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = "#e8e8e8";
                          e.currentTarget.style.color = "#333";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                          e.currentTarget.style.color = "#666";
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="use-template-btn"
                        onClick={e => handleUseTemplate(e, template)}
                        style={{
                          backgroundColor: "#ff6b35",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          padding: "10px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          flex: 1,
                          boxShadow: "0 2px 4px rgba(255, 107, 53, 0.2)",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.backgroundColor = "#ff5722";
                          e.currentTarget.style.boxShadow =
                            "0 4px 8px rgba(255, 107, 53, 0.3)";
                          e.currentTarget.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.backgroundColor = "#ff6b35";
                          e.currentTarget.style.boxShadow =
                            "0 2px 4px rgba(255, 107, 53, 0.2)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        Send Document
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {showUploadModal && (
          <UploadTemplateModal
            onClose={() => setShowUploadModal(false)}
            onSuccess={() => {
              setShowUploadModal(false);
              loadTemplates();
            }}
          />
        )}
      </div>
    </PageContent>
  );
};

const UploadTemplateModal = ({ onClose, onSuccess }) => {
  const { getVerifiedJWT } = useAuth();
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    if (!file || !name) {
      alert("Please provide a name and select a file");
      return;
    }

    try {
      setUploading(true);
      const jwt = await getVerifiedJWT();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);

      const result = await esignatureService.createTemplate(formData, jwt);
      const templateId = result?.data?.id || result?.id;

      if (!templateId) {
        throw new Error("Failed to get template ID from response");
      }

      navigate(`/esignature/templates/${templateId}/edit`);
      onSuccess();
    } catch (error) {
      alert("Failed to create template");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Create Template</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>PDF File *</label>
            <input
              type="file"
              accept=".pdf"
              onChange={e => setFile(e.target.files[0])}
              required
            />
            {file && (
              <div className="file-info">
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={uploading || !file || !name}
            >
              {uploading ? "Creating..." : "Create Template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateList;
